import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Wallet, TrendingUp, Calendar, Users, AlertTriangle } from 'lucide-react';
import { formatCommissionPercentage } from '../../utils/commissionLimits';
import {
  reportsApi,
  CashFlowReportDto,
  ReportFilterDto,
  formatCurrency,
  handleApiError
} from '../../services/reportsService';

const ReportContainer = styled.div`
  padding: 0;
`;

const SummaryCards = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-bottom: 32px;

  @media (max-width: 1279px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

const SummaryCard = styled.div<{ $accentColor?: string }>`
  background: white;
  border-radius: 12px;
  padding: 24px;
  padding-top: 28px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  border: 1px solid var(--border-color);
  position: relative;
  overflow: hidden;
  transition: all 0.2s ease;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${props => props.$accentColor || 'var(--primary-color)'};
    filter: saturate(0.7) brightness(0.9);
  }

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    border-color: var(--border-color);
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const CardIcon = styled.div<{ $color?: string }>`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${props => props.$color ? `${props.$color}15` : 'var(--primary-light)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.$color || 'var(--primary-color)'};
  filter: saturate(0.7) brightness(0.9);
`;

const CardTitle = styled.div`
  font-size: 14px;
  color: var(--text-secondary);
  font-weight: 500;
`;

const CardValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
  direction: ltr;
  text-align: right;

  @media (max-width: 767px) {
    font-size: 22px;
  }
`;

const CardSubtitle = styled.div`
  margin-top: 6px;
  font-size: 13px;
  color: var(--text-secondary);
`;

const NoPlanBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  color: #b45309;
  background: rgba(245, 158, 11, 0.12);
  border: 1px solid rgba(245, 158, 11, 0.35);
`;

const TableContainer = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  border: 1px solid var(--border-color);
  padding: 24px;
  overflow-x: auto;
`;

const TableHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
`;

const TableIcon = styled.div`
  color: var(--primary-color);
  display: flex;
  align-items: center;
`;

const TableTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 800px;
`;

const Th = styled.th`
  background: var(--background-color);
  padding: 16px 20px;
  text-align: center;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  border-bottom: 1px solid var(--border-color);
`;

const Td = styled.td`
  padding: 16px 20px;
  text-align: center;
  font-size: 14px;
  color: var(--text-primary);
  border-bottom: 1px solid var(--border-color);
  vertical-align: middle;
`;

const Tr = styled.tr`
  transition: background-color 0.15s ease;

  &:hover {
    background: var(--background-color);
  }

  &:last-child td {
    border-bottom: none;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  color: var(--text-secondary);
  font-size: 15px;
`;

const ErrorContainer = styled.div`
  background: #fef2f2;
  color: var(--error-color);
  padding: 16px 20px;
  border-radius: 10px;
  border: 1px solid #fecaca;
  font-size: 14px;
`;

const DistributionSection = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  border: 1px solid var(--border-color);
  padding: 24px;
  margin-top: 24px;
`;

const DistributionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
`;

const DistributionIcon = styled.div`
  color: var(--primary-color);
  display: flex;
  align-items: center;
`;

const DistributionTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
`;

const MonthBar = styled.div`
  background: white;
  border: 1px solid var(--border-color);
  border-radius: 10px;
  padding: 16px 20px;
  margin-bottom: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--primary-color);
    box-shadow: 0 2px 8px rgba(0, 142, 162, 0.1);
  }

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 767px) {
    padding: 14px 16px;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

const MonthLabel = styled.div`
  font-size: 15px;
  font-weight: 500;
  color: var(--text-primary);

  @media (max-width: 767px) {
    font-size: 14px;
  }
`;

const MonthAmount = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: var(--primary-color);
  direction: ltr;
  filter: saturate(0.7) brightness(0.85);

  @media (max-width: 767px) {
    font-size: 16px;
  }
`;

interface CashFlowReportProps {
  filter: ReportFilterDto;
}

const CashFlowReport: React.FC<CashFlowReportProps> = ({ filter }) => {
  const [report, setReport] = useState<CashFlowReportDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const loadReport = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await reportsApi.getCashFlowReport(filter);
      setReport(data);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingContainer>טוען דוח תזרים מזומנים...</LoadingContainer>;
  }

  if (error) {
    return <ErrorContainer>{error}</ErrorContainer>;
  }

  if (!report) {
    return null;
  }

  return (
    <ReportContainer>
      <SummaryCards>
        <SummaryCard $accentColor="var(--success-color)">
          <CardHeader>
            <CardIcon $color="var(--success-color)">
              <Wallet size={20} />
            </CardIcon>
            <CardTitle>כסף שנכנס החודש (בפועל, כולל מע"מ)</CardTitle>
          </CardHeader>
          <CardValue>{formatCurrency(report.monthlyActualIncome)}</CardValue>
        </SummaryCard>

        <SummaryCard $accentColor="#6366f1">
          <CardHeader>
            <CardIcon $color="#6366f1">
              <TrendingUp size={20} />
            </CardIcon>
            <CardTitle>הכנסה צפויה חודש הבא</CardTitle>
          </CardHeader>
          <CardValue>{formatCurrency(report.nextMonthExpectedIncome)}</CardValue>
        </SummaryCard>

        <SummaryCard $accentColor="var(--warning-color)">
          <CardHeader>
            <CardIcon $color="var(--warning-color)">
              <Calendar size={20} />
            </CardIcon>
            <CardTitle>הכנסה צפויה 3 חודשים קדימה</CardTitle>
          </CardHeader>
          <CardValue>{formatCurrency(report.threeMonthsExpectedIncome)}</CardValue>
        </SummaryCard>

        <SummaryCard $accentColor="var(--warning-color)">
          <CardHeader>
            <CardIcon $color="var(--warning-color)">
              <AlertTriangle size={20} />
            </CardIcon>
            <CardTitle>חוב ללא תוכנית תשלומים</CardTitle>
          </CardHeader>
          <CardValue>{formatCurrency(report.clientsWithoutPlanTotal)}</CardValue>
          <CardSubtitle>
            {report.clientsWithoutPlanCount} לקוחות · לא נכלל בצפי
          </CardSubtitle>
        </SummaryCard>
      </SummaryCards>

      <TableContainer>
        <TableHeader>
          <TableIcon>
            <Users size={18} />
          </TableIcon>
          <TableTitle>פירוט לפי לקוח</TableTitle>
        </TableHeader>
        <Table>
          <thead>
            <tr>
              <Th>שם לקוח</Th>
              <Th>גמלה חודשית</Th>
              <Th>אחוז עמלה</Th>
              <Th>סה"כ לגביה</Th>
              <Th>תשלומים שנותרו</Th>
              <Th>הכנסה חודשית צפויה</Th>
            </tr>
          </thead>
          <tbody>
            {report.customerDetails.map((customer) => (
              <Tr key={customer.clientId}>
                <Td style={{ fontWeight: 600 }}>
                  {customer.customerName}
                </Td>
                <Td>{customer.monthlyBenefitAmount != null ? formatCurrency(customer.monthlyBenefitAmount) : '—'}</Td>
                <Td>{formatCommissionPercentage(customer.commissionPercentage)}%</Td>
                <Td>{formatCurrency(customer.totalToCollect)}</Td>
                <Td>
                  {customer.hasPaymentPlan ? (
                    customer.remainingInstallments
                  ) : (
                    <NoPlanBadge title="אין תוכנית תשלומים — החוב אינו נכלל בצפי">
                      <AlertTriangle size={12} />
                      ללא תוכנית תשלומים
                    </NoPlanBadge>
                  )}
                </Td>
                <Td style={customer.hasPaymentPlan
                  ? { fontWeight: 600, color: '#16a34a', filter: 'saturate(0.7) brightness(0.85)' }
                  : { color: 'var(--text-secondary)' }}>
                  {customer.hasPaymentPlan ? formatCurrency(customer.expectedMonthlyIncome) : '—'}
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      </TableContainer>

      <DistributionSection>
        <DistributionHeader>
          <DistributionIcon>
            <Calendar size={18} />
          </DistributionIcon>
          <DistributionTitle>התפלגות הכנסה צפויה - 6 חודשים קדימה</DistributionTitle>
        </DistributionHeader>
        {report.monthlyForecast.map((month) => (
          <MonthBar key={month.monthOffset}>
            <MonthLabel>{month.monthName}</MonthLabel>
            <MonthAmount>{formatCurrency(month.expectedAmount)}</MonthAmount>
          </MonthBar>
        ))}
      </DistributionSection>
    </ReportContainer>
  );
};

export default CashFlowReport;
